import { useMemo } from 'react'
import { isClosedProgressType } from '../constants'
import type { EnrichedTask } from '@/lib/db/rxdb/hooks/entities/tasks'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import { useChecklistItems, useChecklists, useTasks } from '@/lib/db/rxdb/hooks'

export type TaskRowCounts = {
  checklistDone: number
  checklistTotal: number
  subTaskDone: number
  subTaskTotal: number
}

const EMPTY_COUNTS: TaskRowCounts = {
  checklistDone: 0,
  checklistTotal: 0,
  subTaskDone: 0,
  subTaskTotal: 0,
}

/**
 * The parent in a list (design 28b): per-task checklist and sub-task counts,
 * plus each parent's sub-tasks in order, batch-computed once per list so rows
 * never subscribe on their own. Checklist items are only ever counted.
 */
export type TaskRowCountsData = ReturnType<typeof useTaskRowCounts>

export function useTaskRowCounts({ enabled = true }: { enabled?: boolean } = {}) {
  // Only sub-tasks: the entity hook passes filter values straight into the
  // RxDB selector, so this skips every top-level task.
  const { tasks } = useTasks(
    { isDeleted: false, parentId: { $ne: null } } as unknown as Partial<TaskDocument>,
    { enabled },
  )
  const { checklists } = useChecklists({ isDeleted: false }, { enabled })
  const { checklistItems } = useChecklistItems({ isDeleted: false }, { enabled })

  const subTasksByParent = useMemo(() => {
    const map = new Map<string, Array<EnrichedTask>>()
    for (const task of tasks) {
      if (!task.parentId || task.isDeleted || task.isArchived) continue
      const list = map.get(task.parentId)
      if (list) list.push(task)
      else map.set(task.parentId, [task])
    }
    // Same order as the task panel's Sub-tasks section.
    for (const list of map.values()) {
      list.sort((a, b) =>
        a.sortOrder && b.sortOrder ? a.sortOrder.localeCompare(b.sortOrder) : a.createdOn - b.createdOn,
      )
    }
    return map
  }, [tasks])

  const countsByTask = useMemo(() => {
    const map = new Map<string, TaskRowCounts>()
    const ensure = (taskId: string): TaskRowCounts => {
      let entry = map.get(taskId)
      if (!entry) {
        entry = { ...EMPTY_COUNTS }
        map.set(taskId, entry)
      }
      return entry
    }

    for (const [parentId, subTasks] of subTasksByParent) {
      const entry = ensure(parentId)
      entry.subTaskTotal = subTasks.length
      entry.subTaskDone = subTasks.filter((st) => isClosedProgressType(st.status?.progressType)).length
    }

    const checklistTask = new Map<string, string>()
    for (const checklist of checklists) {
      if (!checklist.isDeleted) checklistTask.set(checklist.id, checklist.taskId)
    }
    for (const item of checklistItems) {
      if (item.isDeleted) continue
      const taskId = checklistTask.get(item.checklistId)
      if (!taskId) continue
      const entry = ensure(taskId)
      entry.checklistTotal += 1
      if (item.isCompleted) entry.checklistDone += 1
    }

    return map
  }, [subTasksByParent, checklists, checklistItems])

  return { countsByTask, subTasksByParent }
}
